import { mdiClose, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import dateFns from 'date-fns';
import React, { useContext } from 'react';
import { Row } from 'react-table';
import { MessagingContext } from '../../services';
import { SidePanel } from '../../ui';
import { ActiveFilterItem, DataTablePresenter, ICustomFilter, ITableColumn, ITableFetchOptions } from '../data-table';
import * as S from './message-list.styles';
import { getNickname, IMessagingAuthor, IMessagingMessage, isLastAuthor } from './util';

interface IMessageListProps {
    inboxCount: number;
    inboxMessages: IMessagingMessage[];
    onFetchData: (newTableState: ITableFetchOptions) => Promise<void>;
    onShowCompose: () => void;
    onFilterRemove?: () => void;
    selectedMessageId: string | undefined;
    filter?: ICustomFilter;
}

export const MessageListPanel: React.FunctionComponent<IMessageListProps> = (
    props: IMessageListProps,
): React.ReactElement => {
    const { filter, inboxMessages, onFetchData, selectedMessageId, onShowCompose, onFilterRemove } = props;

    const context = useContext(MessagingContext);
    const { isReadOnly } = context;

    const pageSize = 100;

    const formatDate = (d: Date): string => dateFns.format(d, 'MMM D, YYYY');
    const parseDate = (d: string | undefined): Date => (d ? dateFns.parse(d) : new Date());

    const columns: ITableColumn[] = [
        {
            id: 'From',
            Header: 'From',
            Cell: ({ row }): React.ReactElement => {
                const { author, comments } = row.original;
                return (
                    <>
                        {renderFromField(row.original, author)}
                        {comments > 1 && <S.CommentCount>{comments}</S.CommentCount>}
                    </>
                );
            },
            maxWidth: 200,
        },
        {
            id: 'Subject',
            Header: 'Subject',
            Cell: ({ row }): React.ReactElement => {
                const { documents, subject, read } = row.original;
                return (
                    <S.SubjectField unread={!read}>
                        {subject}
                        {documents > 0 && <S.AttachmentField count={documents} />}
                    </S.SubjectField>
                );
            },
        },
        {
            id: 'Date',
            Header: 'Date',
            accessor: 'updated',
            Cell: ({ cell }): string => formatDate(parseDate(cell.value)),
            maxWidth: 200,
        },
    ];

    const renderFromField = (message: IMessagingMessage, author: IMessagingAuthor): React.ReactElement => {
        return (
            <>
                <S.IndicatorField unread={!message.read && isLastAuthor(message, author)} author={author} />
                <S.FromField unread={!message.read && isLastAuthor(message, author)}>{getNickname(author)}</S.FromField>
            </>
        );
    };

    const onRowClick = () => {
        const { onMessageClose, onMessageOpen } = context;
        const selectedId = selectedMessageId;
        return async (row: Row<IMessagingMessage>): Promise<void> => {
            const {
                original: { id },
            } = row;
            if (id === selectedId) {
                await (onMessageClose && onMessageClose());
            } else {
                await (onMessageOpen && id && onMessageOpen(id));
            }
        };
    };

    const onCloseClick = (): void => {
        context.onMessageClose && context.onMessageClose();
    };

    const presenter = new DataTablePresenter({
        name: 'message-list-panel',
        columns,
        data: inboxMessages,
        filterSchemas: [],
    });

    return (
        <SidePanel>
            <S.MessageListPanel>
                <S.Header>
                    <S.ComposeButton disabled={isReadOnly} variant="primary" onClick={onShowCompose}>
                        <S.ComposeIcon path={mdiPlus} size={0.75} />
                        <S.ComposeLabel>Start new message</S.ComposeLabel>
                    </S.ComposeButton>
                    <S.CloseButton variant="fab-light" onClick={onCloseClick}>
                        <S.CloseIcon path={mdiClose} size={1} />
                    </S.CloseButton>
                </S.Header>
                {!!filter && (
                    <div>
                        <S.FilterButton
                            variant="text"
                            onClick={onFilterRemove ? onFilterRemove : undefined}
                            disabled={!!filter.locked}>
                            <ActiveFilterItem filter={filter} />
                            {!filter.locked && <Icon path={mdiClose} size={0.75} />}
                        </S.FilterButton>
                    </div>
                )}
                {/* {filter?.filterId} */}
                <S.TableContainer>
                    <S.MessageTable
                        presenter={presenter}
                        isLoading={context.isListLoading}
                        onFetch={onFetchData}
                        onRowClick={onRowClick()}
                        hideFiltering={true}
                        hideNavigation={true}
                        hidePagination={true}
                        disableSorting={true}
                        hideSettings={true}
                    />
                </S.TableContainer>
            </S.MessageListPanel>
        </SidePanel>
    );
};
